import { CrossFade } from 'base/ui/cross_fade';
import { Rows } from 'base/ui/layout';
import logoRelume from 'base/ui/logo.file.svg';
import { TextMedium, TextRegular, TextRegularLight } from 'base/ui/typography';
import classNames from 'classnames';
import React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { default as LogoIdeo } from '../billing//logos/ideo.react.svg';
import { default as LogoNike } from '../billing//logos/nike.react.svg';
import { default as LogoRokt } from '../billing//logos/rokt.react.svg';
import { default as LogoVmg } from '../billing//logos/vmg.react.svg';
import { default as LogoWebflow } from '../billing//logos/webflow.react.svg';
import { default as LogoDept } from '../billing/logos/dept.react.svg';
import styles from './layout.css';
import photoJordan from './photo-jordan.webp';
import photoRan from './photo-ran.webp';

export function AuthNavbar({ floatLeft = false }: { floatLeft?: boolean }) {
  return (
    <div
      className={classNames(
        styles.logoContainer,
        floatLeft && styles.floatLeft,
      )}
    >
      <a href="/">
        <img
          src={logoRelume}
          className={styles.logo}
          alt="Relume Library"
          role="presentation"
        />
      </a>
    </div>
  );
}

export function AuthLayout({
  children,
  transitionKey,
  showQuote,
  contentFullWidth,
  isFinishing,
}: {
  children: React.ReactNode;
  transitionKey: string;
  showQuote: boolean;
  contentFullWidth: boolean;
  isFinishing: boolean;
}) {
  const childRef = React.useMemo(
    () => React.createRef<HTMLDivElement>(),
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transitionKey],
  );
  const columnsLayout = (
    <div
      className={classNames(
        styles.layout,
        showQuote && styles.showQuote,
        isFinishing && styles.isFinishing,
      )}
    >
      <div className={classNames(styles.layoutContent)}>
        <TransitionGroup component={null} appear={true}>
          <CSSTransition
            key={transitionKey}
            classNames={styles}
            in={true}
            timeout={1000}
          >
            <div className={styles.layoutContentTransition} ref={childRef}>
              <div className={styles.layoutContentPanel}>{children}</div>
            </div>
          </CSSTransition>
        </TransitionGroup>
      </div>
      <div className={styles.layoutQuote}>
        <Quote />
      </div>
    </div>
  );
  const fullWidthLayout = (
    <div className={styles.fullWidthLayout}>{children}</div>
  );
  return (
    <CrossFade transitionKey={contentFullWidth ? 't' : 'f'}>
      {contentFullWidth ? fullWidthLayout : columnsLayout}
    </CrossFade>
  );
}

const ALL_QUOTES = [
  {
    text: 'Nothing short of groundbreaking for designers. It immediately changes how I work and makes me 10x more productive as a web designer.',
    author: '@ransegall',
    jobTitle: 'Founder, Flux Academy',
    photoUrl: photoRan,
  },
  {
    text: 'The scale, quality, and organisation of this library is insane! Everything you need to spin up responsive designs in Figma and Webflow in no time so you can focus on the details.',
    author: '@jordanphughes',
    jobTitle: 'Co-founder, Untitled UI',
    photoUrl: photoJordan,
  },
];

function Quote() {
  const quote = React.useMemo(
    () => {
      const index = Math.floor(Math.random() * ALL_QUOTES.length);
      return ALL_QUOTES[index];
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ALL_QUOTES],
  );
  return (
    <div className={styles.quote}>
      <Rows spacing={64}>
        <Rows spacing={16} xalign="center">
          <img
            src={quote.photoUrl}
            className={styles.profilePhoto}
            alt={'Photo of ' + quote.author}
          />
          <div />
          <TextMedium align="center">{quote.text}</TextMedium>
          <Rows spacing={4} xalign="center">
            <TextRegular align="center">{quote.author}</TextRegular>
            <TextRegularLight color="grey" align="center">
              {quote.jobTitle}
            </TextRegularLight>
          </Rows>
        </Rows>
        <Rows spacing={16} xalign="center">
          <TextRegularLight align="center">
            <span className={styles.gradientText}>400,000+</span> designers &
            developers have used Relume Library to build websites faster.
          </TextRegularLight>
          <div className={styles.quoteLogos}>
            <Logo svg={LogoNike} />
            <Logo svg={LogoWebflow} />
            <Logo svg={LogoRokt} />
            <Logo svg={LogoIdeo} />
            <Logo svg={LogoDept} />
            <Logo svg={LogoVmg} />
          </div>
        </Rows>
      </Rows>
    </div>
  );
}

function Logo({
  svg: Svg,
}: {
  svg: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) {
  return <Svg className={styles.quoteLogo} />;
}
